import { Handle, Position } from 'reactflow';
import { Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DataTestData } from '@simcheck/shared-types';

interface DataTestNodeData {
  label?: string;
  dataTest?: Partial<DataTestData>;
}

// The most descriptive single field to surface in the node body per sub-type.
function summarizeTarget(d: Partial<DataTestData> | undefined): string | null {
  if (!d) return null;
  return (
    d.url ||
    d.host ||
    d.target ||
    d.hostname ||
    d.domain ||
    (d.apns && d.apns.length ? d.apns.join(', ') : null) ||
    d.apn ||
    null
  );
}

export default function DataTestNode({
  data,
  selected,
}: {
  data: DataTestNodeData;
  selected?: boolean;
}) {
  const dt = data.dataTest;
  const target = summarizeTarget(dt);

  return (
    <div className={cn(
      "min-w-[170px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? "border-category-data shadow-glow-focus"
        : "border-category-data/50 dark:border-category-data/30 shadow-md hover:shadow-lg"
    )}>
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-category-data !border-category-data"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-category-data/10 dark:bg-category-data/20 flex items-center justify-center">
          <Activity className="w-3.5 h-3.5 text-category-data" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Data Test
          </div>
          <div className="text-xs text-muted-foreground/70 font-mono truncate">
            {dt?.testType || 'Not configured'}
          </div>
        </div>
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {target && (
          <div className="text-xs">
            <span className="text-muted-foreground font-medium">Target:</span>
            <span className="ml-1 text-foreground font-mono truncate">{target}</span>
          </div>
        )}

        {dt?.storeAs && (
          <div className="text-xs">
            <span className="text-category-data font-medium font-mono">
              → ${dt.storeAs}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-border dark:border-border-subtle">
          <span className="text-xs text-muted-foreground/70">
            {dt?.timeout ? `${dt.timeout}ms` : '60000ms'}
          </span>
          {dt?.count !== undefined && dt.count > 0 && (
            <span className="text-xs text-muted-foreground/70">
              ×{dt.count}
            </span>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-category-data !border-category-data"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}
